/**
 * ═══════════════════════════════════════════════════════════════════════════
 * RiskGauge - Gauge Circular SVG Animado
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @file RiskGauge.tsx
 * @version 1.0.0
 * @description Gauge circular que muestra el risk score (0-100) con animación
 *              smooth y colores según el nivel de riesgo
 * 
 * @component RiskGauge
 * @architecture Fase 2 - Componentes Visuales del Risk Meter
 * 
 * FEATURES:
 * - Gauge circular SVG nativo (sin dependencias)
 * - Animación smooth de 0 → score actual
 * - Colores dinámicos según nivel (verde/amarillo/naranja/rojo)
 * - Texto central: score + nivel
 * - Responsive y customizable
 * 
 * COLORES POR NIVEL:
 * - LOW (≥80):      #22c55e (verde)
 * - MEDIUM (60-79): #eab308 (amarillo)
 * - HIGH (40-59):   #f97316 (naranja)
 * - CRITICAL (<40): #dc2626 (rojo)
 */

import React, { useEffect, useState } from 'react';

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

type RiskLevel = 'low' | 'medium' | 'high' | 'critical';

interface RiskGaugeProps {
  /** Score de riesgo (0-100) */
  score: number;
  
  /** Nivel de riesgo */
  level: RiskLevel;
  
  /** Tamaño del gauge en píxeles (default: 200) */
  size?: number;
  
  /** Habilitar animación (default: true) */
  animated?: boolean;
  
  /** Mostrar texto del nivel (default: true) */
  showLabel?: boolean;
  
  /** Clase CSS adicional */
  className?: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// CONSTANTES
// ═══════════════════════════════════════════════════════════════════════════

const LEVEL_COLORS: Record<RiskLevel, string> = {
  low: '#22c55e',      // Verde
  medium: '#eab308',   // Amarillo
  high: '#f97316',     // Naranja
  critical: '#dc2626'  // Rojo
};

const LEVEL_LABELS: Record<RiskLevel, string> = {
  low: 'Bajo',
  medium: 'Medio',
  high: 'Alto',
  critical: 'Crítico'
};

const LEVEL_BG_COLORS: Record<RiskLevel, string> = {
  low: '#22c55e10',
  medium: '#eab30810',
  high: '#f9731610',
  critical: '#dc262610'
};

// ═══════════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Calcula el ángulo del arco basado en el score
 * Score 0 = 0°, Score 100 = 270° (3/4 de círculo)
 */
const calculateArcAngle = (score: number): number => {
  return (Math.min(100, Math.max(0, score)) / 100) * 270;
};

/**
 * Crea un path SVG para un arco circular
 */
const createArcPath = (
  cx: number,
  cy: number,
  radius: number,
  startAngle: number,
  endAngle: number
): string => {
  const start = polarToCartesian(cx, cy, radius, endAngle);
  const end = polarToCartesian(cx, cy, radius, startAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';
  
  return [
    'M', start.x, start.y,
    'A', radius, radius, 0, largeArcFlag, 0, end.x, end.y
  ].join(' ');
};

/**
 * Convierte coordenadas polares a cartesianas
 */
const polarToCartesian = (
  centerX: number,
  centerY: number,
  radius: number,
  angleInDegrees: number
): { x: number; y: number } => {
  const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;
  
  return {
    x: centerX + radius * Math.cos(angleInRadians),
    y: centerY + radius * Math.sin(angleInRadians)
  };
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export const RiskGauge: React.FC<RiskGaugeProps> = ({
  score,
  level,
  size = 200,
  animated = true,
  showLabel = true,
  className = ''
}) => {
  // ───────────────────────────────────────────────────────────────────────
  // STATE - Animated score
  // ───────────────────────────────────────────────────────────────────────
  
  const [displayScore, setDisplayScore] = useState(animated ? 0 : score);
  
  // ───────────────────────────────────────────────────────────────────────
  // EFFECTS - Animate score
  // ───────────────────────────────────────────────────────────────────────
  
  useEffect(() => {
    if (!animated) {
      setDisplayScore(score);
      return;
    }
    
    // Animación smooth del score
    const duration = 1500; // 1.5 segundos
    const steps = 60; // 60 frames
    const increment = score / steps;
    const stepDuration = duration / steps;
    
    let currentStep = 0;
    
    const timer = setInterval(() => {
      currentStep++;
      
      if (currentStep >= steps) {
        setDisplayScore(score);
        clearInterval(timer);
      } else {
        setDisplayScore(Math.min(score, increment * currentStep));
      }
    }, stepDuration);
    
    return () => clearInterval(timer);
  }, [score, animated]);
  
  // ───────────────────────────────────────────────────────────────────────
  // CALCULATIONS
  // ───────────────────────────────────────────────────────────────────────
  
  const strokeWidth = size * 0.08; // 8% del tamaño
  const radius = (size - strokeWidth) / 2;
  const center = size / 2;
  
  const arcAngle = calculateArcAngle(displayScore);
  const color = LEVEL_COLORS[level];
  const bgColor = LEVEL_BG_COLORS[level];
  
  // Path del arco de fondo (270° completo)
  const backgroundPath = createArcPath(center, center, radius, 135, 405);
  
  // Path del arco de progreso
  const progressPath = createArcPath(center, center, radius, 135, 135 + arcAngle);
  
  // ───────────────────────────────────────────────────────────────────────
  // RENDER
  // ───────────────────────────────────────────────────────────────────────
  
  return (
    <div 
      className={`risk-gauge ${className}`}
      style={{
        position: 'relative',
        width: `${size}px`,
        height: `${size}px`,
        display: 'inline-block'
      }}
    >
      {/* SVG Gauge */}
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        style={{ 
          display: 'block',
          filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
        }}
      >
        {/* Background circle */}
        <circle
          cx={center}
          cy={center}
          r={radius - strokeWidth / 2}
          fill={bgColor}
          stroke="none"
        />
        
        {/* Background arc (gris claro) */}
        <path
          d={backgroundPath}
          fill="none"
          stroke="var(--bs-border-color, #e5e7eb)"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
        
        {/* Progress arc (coloreado) */}
        <path
          d={progressPath}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          style={{
            transition: animated ? 'stroke-dasharray 0.3s ease' : 'none'
          }}
        />
        
        {/* Texto central - Score */}
        <text
          x={center}
          y={center - 10}
          textAnchor="middle"
          dominantBaseline="middle"
          style={{
            fontSize: `${size * 0.25}px`,
            fontWeight: 700,
            fill: 'var(--bs-body-color, #1f2937)',
            fontFamily: 'system-ui, -apple-system, sans-serif'
          }}
        >
          {Math.round(displayScore)}
        </text>
        
        {/* Texto central - Label */}
        {showLabel && (
          <text
            x={center}
            y={center + (size * 0.15)}
            textAnchor="middle"
            dominantBaseline="middle"
            style={{
              fontSize: `${size * 0.08}px`,
              fontWeight: 600,
              fill: color,
              fontFamily: 'system-ui, -apple-system, sans-serif',
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}
          >
            {LEVEL_LABELS[level]}
          </text>
        )}
        
        {/* Marcadores de escala (0, 50, 100) */}
        <g style={{ opacity: 0.3 }}>
          {/* 0 */}
          <text
            x={center - radius * 0.7}
            y={center + radius * 0.7 + 5}
            textAnchor="middle"
            style={{
              fontSize: `${size * 0.06}px`,
              fill: 'var(--bs-secondary, #6b7280)',
              fontFamily: 'system-ui, -apple-system, sans-serif'
            }}
          >
            0
          </text>
          
          {/* 50 */}
          <text
            x={center}
            y={center - radius - 5}
            textAnchor="middle"
            style={{
              fontSize: `${size * 0.06}px`,
              fill: 'var(--bs-secondary, #6b7280)',
              fontFamily: 'system-ui, -apple-system, sans-serif'
            }}
          >
            50
          </text>
          
          {/* 100 */}
          <text
            x={center + radius * 0.7}
            y={center + radius * 0.7 + 5}
            textAnchor="middle"
            style={{
              fontSize: `${size * 0.06}px`,
              fill: 'var(--bs-secondary, #6b7280)',
              fontFamily: 'system-ui, -apple-system, sans-serif'
            }}
          >
            100
          </text>
        </g>
      </svg>
      
      {/* CSS for animations */}
      <style>{`
        .risk-gauge {
          user-select: none;
        }
        
        .risk-gauge svg {
          overflow: visible;
        }
        
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.8;
          }
        }
      `}</style>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// EXPORT
// ═══════════════════════════════════════════════════════════════════════════

export default RiskGauge;
